<template>
  <div class="ack-pipeline-kanban">
    <div class="kanban-columns">
      <div
        v-for="column in columns"
        :key="column.value"
        class="kanban-column"
      >
        <div
          class="kanban-column-header"
          :style="{ borderTopColor: column.color }"
        >
          <span class="column-title">{{ column.label }}</span>
          <b-badge
            :variant="column.badgeVariant"
            pill
          >
            {{ (columnLists[column.value] || []).length }}
          </b-badge>
        </div>

        <vue-perfect-scrollbar
          :settings="perfectScrollbarSettings"
          class="kanban-column-content"
        >
          <draggable
            :list="columnLists[column.value]"
            :group="{ name: 'ack-pipeline' }"
            handle=".drag-handle"
            class="kanban-cards"
            ghost-class="ghost-card"
            :animation="180"
            @change="onDragChange($event, column.value)"
          >
            <b-card
              v-for="project in columnLists[column.value]"
              :key="project.id"
              class="kanban-card mb-50"
              :class="{ 'can-act-card': project.can_act }"
              body-class="p-50"
            >
              <div class="d-flex align-items-start">
                <feather-icon
                  icon="MoveIcon"
                  size="12"
                  class="drag-handle text-muted mt-25 ml-25 flex-shrink-0"
                />
                <div class="flex-grow-1 min-width-0">
                  <div class="d-flex align-items-start justify-content-between">
                    <div class="min-width-0 pr-25">
                      <div class="card-title-text text-truncate">
                        {{ projectName(project) }}
                      </div>
                      <div
                        v-if="projectCode(project)"
                        class="card-code text-truncate"
                      >
                        {{ projectCode(project) }}
                      </div>
                      <div
                        v-if="projectCenter(project)"
                        class="card-center text-truncate"
                        v-b-tooltip.hover
                        :title="projectCenter(project)"
                      >
                        {{ projectCenter(project) }}
                      </div>
                    </div>
                    <b-badge
                      :variant="statusBadge(project).variant"
                      class="flex-shrink-0 status-badge"
                    >
                      {{ statusBadge(project).label }}
                    </b-badge>
                  </div>

                  <div class="d-flex align-items-center mt-25 flex-wrap">
                    <b-avatar
                      v-for="actor in actors(project).slice(0, 3)"
                      :key="actor.id"
                      :src="actor.avatar || undefined"
                      :text="avatarInitials(actor.name)"
                      :variant="project.can_act && isCurrentUser(actor) ? 'light-success' : 'light-primary'"
                      size="18"
                      class="mr-25"
                      v-b-tooltip.hover
                      :title="actorTooltip(actor)"
                    />
                    <span
                      v-if="actorsOverflow(project)"
                      class="text-muted overflow-count"
                    >+{{ actorsOverflow(project) }}</span>
                    <span
                      v-if="project.can_act"
                      class="can-act-dot"
                      v-b-tooltip.hover
                      title="يمكنك الاقرار"
                    />
                    <span class="history-dots mr-auto">
                      <span
                        v-for="step in historySteps"
                        :key="step"
                        class="history-dot"
                        :class="historyDotClass(project, step)"
                        v-b-tooltip.hover
                        :title="historyTooltip(project, step)"
                      />
                    </span>
                    <b-button
                      v-if="chatId(project)"
                      variant="link"
                      class="p-0 icon-btn"
                      v-b-tooltip.hover
                      title="المحادثة"
                      @click.stop="$emit('open-chat', project)"
                    >
                      <feather-icon
                        icon="MessageSquareIcon"
                        size="13"
                      />
                      <b-badge
                        v-if="unreadCount(project)"
                        variant="danger"
                        pill
                        class="unread-badge"
                      >
                        {{ unreadCount(project) }}
                      </b-badge>
                    </b-button>
                    <b-button
                      variant="link"
                      class="p-0 icon-btn"
                      v-b-tooltip.hover
                      title="فتح المشروع في تاب جديد"
                      @click.stop="openProjectInNewTab(project)"
                    >
                      <feather-icon
                        icon="ExternalLinkIcon"
                        size="13"
                      />
                    </b-button>
                  </div>
                </div>
              </div>
            </b-card>
          </draggable>

          <div
            v-if="!(columnLists[column.value] && columnLists[column.value].length)"
            class="empty-column"
          >
            لا توجد مشاريع
          </div>
        </vue-perfect-scrollbar>
      </div>
    </div>

    <b-modal
      v-model="showMoveModal"
      :title="moveModalTitle"
      centered
      no-close-on-backdrop
      @hidden="onMoveModalHidden"
    >
      <p class="mb-1">
        لنقل
        <strong>{{ pendingProjectName }}</strong>
        من
        <strong>{{ stepLabel(pendingFromStep) }}</strong>
        إلى
        <strong>{{ stepLabel(pendingTargetStep) }}</strong>
        يجب إقرار المرحلة الحالية.
      </p>
      <p class="text-muted small mb-1">
        الحفظ يبقي المشروع في مرحلته الحالية كمسودة. حفظ وارسال ينقل المشروع بعد نجاح الارسال.
      </p>

      <b-form-checkbox
        v-model="acknowledged"
        class="mb-1"
        :disabled="saving || sending"
      >
        {{ acknowledgmentText }}
      </b-form-checkbox>

      <b-form-group
        label="المستلمون"
        label-for="ack-pipeline-recipients"
        :description="recipientsDescription"
      >
        <v-select
          id="ack-pipeline-recipients"
          v-model="selectedRecipients"
          :options="recipientOptions"
          :reduce="option => option.id"
          :get-option-label="option => optionLabel(option)"
          :dir="isRtl ? 'rtl' : 'ltr'"
          :filterable="false"
          :loading="loadingRecipients"
          :disabled="saving || sending"
          multiple
          placeholder="ابحث واختر المستلمين..."
          @search="onRecipientSearch"
        >
          <template #option="option">
            <div>
              <span>{{ optionLabel(option) }}</span>
              <small
                v-if="option.email"
                class="text-muted d-block"
              >{{ option.email }}</small>
            </div>
          </template>
          <template #selected-option="option">
            <span>{{ optionLabel(option) }}</span>
          </template>
          <template #no-options>
            لا توجد نتائج
          </template>
        </v-select>
      </b-form-group>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="saving || sending"
          @click="cancel()"
        >
          إلغاء
        </b-button>
        <b-button
          variant="primary"
          :disabled="!acknowledged || saving || sending"
          @click="submitMove('save')"
        >
          <b-spinner
            v-if="saving"
            small
          />
          <span v-else>حفظ</span>
        </b-button>
        <b-button
          variant="success"
          :disabled="!acknowledged || !selectedRecipients.length || saving || sending"
          @click="submitMove('send')"
        >
          <b-spinner
            v-if="sending"
            small
          />
          <span v-else>حفظ وارسال</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BBadge,
  BAvatar,
  BButton,
  BModal,
  BFormCheckbox,
  BFormGroup,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getAuthenticatedUserId } from '@/auth/utils'
import {
  saveSurveyorAcknowledgment,
  saveAndSendSurveyorAcknowledgment,
  getSurveyorAcknowledgmentRecipients,
} from '@/api/projects/surveyorAcknowledgment'
import {
  saveGiserAcknowledgment,
  saveAndSendGiserAcknowledgment,
  getGiserAcknowledgmentRecipients,
} from '@/api/projects/giserAcknowledgment'
import {
  saveGeodesiaAcknowledgment,
  saveAndSendGeodesiaAcknowledgment,
  getGeodesiaAcknowledgmentRecipients,
} from '@/api/projects/geodesiaAcknowledgment'

const STEP_ORDER = ['surveyor', 'giser', 'geodesia', 'completed']
const STEP_LABELS = {
  surveyor: 'اقرار المساح',
  giser: 'منطقية الرفع',
  geodesia: 'اقرار الجيوديسيا',
  completed: 'مكتملة',
}
const STEP_APIS = {
  surveyor: {
    save: saveSurveyorAcknowledgment,
    send: saveAndSendSurveyorAcknowledgment,
    recipients: getSurveyorAcknowledgmentRecipients,
    defaultText: 'اقرار المساح بأنه القائم علي اعمال الرفع المساحي و المسؤل عنه',
    defaultRecipientRoles: ['gisser مكتب فني', 'Giser 168'],
  },
  giser: {
    save: saveGiserAcknowledgment,
    send: saveAndSendGiserAcknowledgment,
    recipients: getGiserAcknowledgmentRecipients,
    defaultText: 'منطقية الرفع المساحي',
    defaultRecipientRoles: ['مساح بالطبيعة'],
  },
  geodesia: {
    save: saveGeodesiaAcknowledgment,
    send: saveAndSendGeodesiaAcknowledgment,
    recipients: getGeodesiaAcknowledgmentRecipients,
    defaultText: 'اقرار الجيوديسيا',
    defaultRecipientRoles: ['gisser مكتب فني', 'Giser 168'],
  },
}

export default {
  name: 'AcknowledgmentPipelineKanbanView',
  components: {
    BCard,
    BBadge,
    BAvatar,
    BButton,
    BModal,
    BFormCheckbox,
    BFormGroup,
    BSpinner,
    vSelect,
    draggable,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    projects: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
      },
      historySteps: ['surveyor', 'giser', 'geodesia'],
      columnLists: {
        surveyor: [],
        giser: [],
        geodesia: [],
        completed: [],
      },
      showMoveModal: false,
      moveCommitted: false,
      pendingProject: null,
      pendingFromStep: null,
      pendingTargetStep: null,
      acknowledged: true,
      saving: false,
      sending: false,
      recipientOptions: [],
      selectedRecipients: [],
      loadingRecipients: false,
      recipientSearchTimer: null,
      columns: [
        {
          value: 'surveyor',
          label: 'اقرار المساح',
          color: '#FF9F43',
          badgeVariant: 'light-warning',
        },
        {
          value: 'giser',
          label: 'منطقية الرفع',
          color: '#00CFE8',
          badgeVariant: 'light-info',
        },
        {
          value: 'geodesia',
          label: 'الجيوديسيا',
          color: '#06284D',
          badgeVariant: 'light-primary',
        },
        {
          value: 'completed',
          label: 'مكتملة',
          color: '#28C76F',
          badgeVariant: 'light-success',
        },
      ],
    }
  },
  computed: {
    currentUserId() {
      return getAuthenticatedUserId()
    },
    isRtl() {
      return !!(this.$store && this.$store.state && this.$store.state.appConfig
        && (this.$store.state.appConfig.layout?.isRTL || this.$store.state.appConfig.isRTL))
    },
    moveModalTitle() {
      return `اقرار: ${this.stepLabel(this.pendingFromStep)}`
    },
    pendingProjectName() {
      return this.pendingProject ? this.projectName(this.pendingProject) : ''
    },
    acknowledgmentText() {
      if (this.pendingProject && this.pendingProject.current_step_label) {
        return this.pendingProject.current_step_label
      }
      const api = STEP_APIS[this.pendingFromStep]
      return (api && api.defaultText) || 'اقرار المرحلة الحالية'
    },
    recipientsDescription() {
      const roles = (this.pendingProject && Array.isArray(this.pendingProject.recipient_roles)
        && this.pendingProject.recipient_roles.length)
        ? this.pendingProject.recipient_roles
        : ((STEP_APIS[this.pendingFromStep] && STEP_APIS[this.pendingFromStep].defaultRecipientRoles) || [])
      return roles.length
        ? `مطلوب لحفظ وارسال (${roles.join(' أو ')})`
        : 'اختر مستلماً واحداً على الأقل لحفظ وارسال'
    },
  },
  watch: {
    projects: {
      immediate: true,
      handler() {
        if (this.showMoveModal) return
        this.syncLists()
      },
    },
  },
  beforeDestroy() {
    if (this.recipientSearchTimer) clearTimeout(this.recipientSearchTimer)
  },
  methods: {
    syncLists() {
      const lists = {
        surveyor: [],
        giser: [],
        geodesia: [],
        completed: [],
      }
      ;(this.projects || []).forEach(project => {
        const step = project.current_step
        if (lists[step]) lists[step].push(project)
      })
      this.columnLists = lists
    },
    stepLabel(step) {
      return STEP_LABELS[step] || step || ''
    },
    onDragChange(event, targetStep) {
      if (!event.added) return
      const project = event.added.element
      const fromStep = project.current_step
      if (fromStep === targetStep) return

      const fromIndex = STEP_ORDER.indexOf(fromStep)
      const toIndex = STEP_ORDER.indexOf(targetStep)
      const isNext = toIndex === fromIndex + 1

      if (!isNext) {
        this.syncLists()
        this.notify(
          'warning',
          'المسار إلزامي: مساح ← منطقية الرفع ← جيوديسيا. لا يمكن تخطي أو الرجوع بالسحب.',
        )
        return
      }

      if (!project.can_act) {
        this.syncLists()
        this.notify(
          'warning',
          this.blockedLabel(project) || 'لا يمكنك إقرار هذه المرحلة',
        )
        return
      }

      this.pendingProject = project
      this.pendingFromStep = fromStep
      this.pendingTargetStep = targetStep
      this.acknowledged = true
      this.selectedRecipients = []
      this.recipientOptions = []
      this.moveCommitted = false
      this.showMoveModal = true
      this.loadRecipients('')
    },
    onMoveModalHidden() {
      this.selectedRecipients = []
      this.recipientOptions = []
      this.pendingProject = null
      this.pendingFromStep = null
      this.pendingTargetStep = null
      this.saving = false
      this.sending = false
      if (!this.moveCommitted) {
        this.syncLists()
      }
      this.moveCommitted = false
    },
    onRecipientSearch(search, loading) {
      if (this.recipientSearchTimer) clearTimeout(this.recipientSearchTimer)
      if (loading) loading(true)
      this.recipientSearchTimer = setTimeout(async () => {
        await this.loadRecipients(search)
        if (loading) loading(false)
      }, 300)
    },
    async loadRecipients(keyword = '') {
      const api = STEP_APIS[this.pendingFromStep]
      const projectId = this.pendingProject && this.pendingProject.id
      if (!api || !projectId) {
        this.recipientOptions = []
        return
      }
      this.loadingRecipients = true
      try {
        const response = await api.recipients(projectId, {
          keyword: keyword || undefined,
        })
        const data = (response && response.data) || response || {}
        this.recipientOptions = Array.isArray(data)
          ? data
          : (Array.isArray(data.data) ? data.data : (Array.isArray(data.users) ? data.users : []))
      } catch (error) {
        this.recipientOptions = []
        this.notify('danger', this.apiErrorMessage(error, 'تعذر تحميل المستلمين'))
      } finally {
        this.loadingRecipients = false
      }
    },
    optionLabel(option) {
      if (!option) return ''
      return option.name || option.full_name || option.email || `#${option.id}`
    },
    unwrapPayload(response) {
      const root = response && response.success !== undefined
        ? response
        : ((response && response.data) || response || {})
      return root.data || root
    },
    extractPipeline(response) {
      const payload = this.unwrapPayload(response)
      if (payload && payload.pipeline && payload.pipeline.id) return payload.pipeline
      if (payload && payload.current_step && payload.id) return payload
      return null
    },
    async submitMove(mode) {
      const api = STEP_APIS[this.pendingFromStep]
      const project = this.pendingProject
      if (!api || !project || !this.acknowledged) return
      if (mode === 'send' && !this.selectedRecipients.length) {
        this.notify('warning', 'يجب اختيار مستلم واحد على الأقل للارسال')
        return
      }

      if (mode === 'send') this.sending = true
      else this.saving = true

      try {
        const body = { acknowledged: true }
        if (mode === 'send') {
          body.recipient_user_ids = this.selectedRecipients.map(id => Number(id))
        }
        const response = mode === 'send'
          ? await api.send(project.id, body)
          : await api.save(project.id, body)

        const data = this.unwrapPayload(response)
        this.moveCommitted = mode === 'send'
        this.showMoveModal = false
        if (mode !== 'send') this.syncLists()

        const message = (response && response.message) || data.message
          || (mode === 'send' ? 'تم الحفظ والارسال ونقل المرحلة' : 'تم الحفظ كمسودة')
        this.notify('success', message)

        if (mode === 'send') {
          const pipeline = this.extractPipeline(response)
          const chat = (pipeline && pipeline.chat) || data.chat
          const chatId = (chat && chat.id) || (pipeline && pipeline.chat_id)
          if (chatId) {
            this.$store.dispatch('surveyChatsUi/open', {
              chatId,
              projectId: project.id,
            })
          }
        }

        this.$emit('refresh')
      } catch (error) {
        this.notify(
          'danger',
          this.apiErrorMessage(error, mode === 'send' ? 'تعذر حفظ وارسال الاقرار' : 'تعذر حفظ الاقرار'),
        )
      } finally {
        this.saving = false
        this.sending = false
      }
    },
    blockedLabel(project) {
      const codes = {
        surveyor_not_sent: 'يجب ارسال اقرار المساح أولاً',
        giser_not_sent: 'يجب ارسال منطقية الرفع أولاً',
        already_sent: 'تم ارسال هذه المرحلة',
        forbidden: 'لا تملك صلاحية النقل',
      }
      return codes[project.blocked_reason] || ''
    },
    apiErrorMessage(error, fallback) {
      const data = error && error.response && error.response.data
      const code = data && data.errors && data.errors.code
      const codes = {
        already_sent: 'تم ارسال هذه المرحلة مسبقاً',
        surveyor_acknowledgment_not_sent: 'يجب ارسال اقرار المساح أولاً',
        giser_acknowledgment_not_sent: 'يجب ارسال منطقية الرفع أولاً',
        invalid_recipients: 'المستلمون غير صالحين لهذه المرحلة',
      }
      return codes[code] || (data && data.message) || fallback || 'حدث خطأ'
    },
    notify(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'نجح' : variant === 'danger' ? 'خطأ' : 'تنبيه',
          icon: variant === 'success' ? 'CheckIcon' : variant === 'danger' ? 'XIcon' : 'AlertTriangleIcon',
          variant,
          text,
        },
      })
    },
    openProjectInNewTab(project) {
      if (!project || !project.id) return
      const route = this.$router.resolve({
        name: 'project_details',
        params: { id: project.id },
      })
      window.open(route.href, '_blank', 'noopener,noreferrer')
    },
    projectName(project) {
      return project.name
        || project.surveying_code
        || project.project_number
        || project.order_code
        || `مشروع #${project.id}`
    },
    projectCode(project) {
      return project.surveying_code || project.project_number || project.order_code || ''
    },
    projectCenter(project) {
      if (!project) return ''
      if (typeof project.center === 'string') return project.center
      const center = project.center || {}
      return center.name_ar
        || center.name
        || center.name_en
        || project.center_name
        || ''
    },
    statusBadge(project) {
      const status = project.step_status
      if (project.current_step === 'completed' || status === 'sent') {
        return { label: 'أرسل', variant: 'light-success' }
      }
      if (status === 'draft') {
        return { label: 'مسودة', variant: 'light-secondary' }
      }
      return { label: 'جديد', variant: 'light-warning' }
    },
    actors(project) {
      return Array.isArray(project.actors) ? project.actors : []
    },
    actorsOverflow(project) {
      const shown = Math.min(3, this.actors(project).length)
      const total = project.actors_count || this.actors(project).length
      return Math.max(0, total - shown)
    },
    isCurrentUser(actor) {
      return !!(this.currentUserId && actor && Number(actor.id) === Number(this.currentUserId))
    },
    actorTooltip(actor) {
      const roles = Array.isArray(actor.roles) && actor.roles.length
        ? actor.roles.join('، ')
        : ''
      return [actor.name, actor.email, roles].filter(Boolean).join(' — ')
    },
    avatarInitials(name) {
      if (!name) return '?'
      return String(name).trim().slice(0, 1)
    },
    historyEntry(project, key) {
      return project.history && project.history[key] ? project.history[key] : null
    },
    historyDotClass(project, key) {
      const entry = this.historyEntry(project, key)
      if (entry && (entry.is_sent || entry.status === 'sent')) return 'is-sent'
      if (entry && entry.status === 'draft') return 'is-draft'
      if (project.current_step === key) return 'is-current'
      return 'is-idle'
    },
    historyTooltip(project, key) {
      const entry = this.historyEntry(project, key)
      const label = STEP_LABELS[key]
      if (entry && (entry.is_sent || entry.status === 'sent')) return `${label}: تم الارسال`
      if (entry && entry.status === 'draft') return `${label}: مسودة`
      if (project.current_step === key) return `${label}: المرحلة الحالية`
      return `${label}: لم يبدأ`
    },
    chatId(project) {
      if (project.chat && project.chat.id) return project.chat.id
      return project.chat_id || null
    },
    unreadCount(project) {
      return (project.chat && project.chat.unread_count) || 0
    },
  },
}
</script>

<style lang="scss" scoped>
.ack-pipeline-kanban {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 0.5rem;
}

.kanban-columns {
  display: flex;
  gap: 0.65rem;
  min-height: calc(100vh - 300px);
}

.kanban-column {
  min-width: 210px;
  /*max-width: 250px; */
  flex: 1;
  background-color: #f4f4f5;
  border-radius: 0.4rem;
  display: flex;
  flex-direction: column;
}

.kanban-column-header {
  padding: 0.45rem 0.55rem;
  border-top: 2px solid;
  border-radius: 0.4rem 0.4rem 0 0;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.column-title {
  font-weight: 600;
  font-size: 0.75rem;
}

.kanban-column-content {
  flex: 1;
  padding: 0.4rem;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
}

.kanban-cards {
  min-height: 64px;
}

.min-width-0 {
  min-width: 0;
}

.kanban-card {
  border: 1px solid #e9ecef;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
}

.can-act-card {
  border-color: #28c76f;
}

.drag-handle {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.card-title-text {
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25;
}

.card-code {
  font-size: 0.65rem;
  color: #6e6b7b;
  line-height: 1.2;
}

.card-center {
  font-size: 0.62rem;
  color: #82868b;
  line-height: 1.2;
}

.status-badge {
  font-size: 0.6rem;
  padding: 0.1rem 0.3rem;
}

.overflow-count {
  font-size: 0.65rem;
}

.can-act-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #28c76f;
  margin: 0 0.25rem;
}

.history-dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-right: 0.35rem;
}

.history-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;

  &.is-sent { background: #28c76f; }
  &.is-draft { background: #82868b; }
  &.is-current { background: #ff9f43; }
  &.is-idle { background: #d8d6de; }
}

.icon-btn {
  line-height: 1;
  min-width: 18px;
  color: #6e6b7b;

  &:hover {
    color: #06284d;
  }
}

.unread-badge {
  font-size: 0.55rem;
  position: relative;
  top: -6px;
  right: 2px;
}

.empty-column {
  text-align: center;
  font-size: 0.7rem;
  color: #b9b9c3;
  padding: 0.5rem 0;
  pointer-events: none;
}

.ghost-card {
  opacity: 0.45;
}

.ack-pipeline-kanban ::v-deep .vs__dropdown-menu {
  z-index: 1100;
}

@media (max-width: 768px) {
  .kanban-columns {
    flex-direction: column;
  }

  .kanban-column {
    max-width: 100%;
    min-width: 100%;
  }
}
</style>
